import { useEffect } from "react";
import { useTableScrollReset } from "@/hooks/useTableScrollReset";
import {
  redirect,
  useLoaderData,
  useNavigation,
  useRevalidator,
  useSearchParams,
  type LoaderFunctionArgs,
} from "react-router-dom";
import { BarChart3 } from "lucide-react";
import { fetchUserProfile } from "@/hooks/useUser";
import { useKeywordVolumeImport } from "@/hooks/useKeywordVolumeImport";
import { ImportTabPanel, ImportProgress } from "@/components/spy-import";
import { queryClient } from "@/lib/queryClient";
import {
  getKeywordVolumesWithDomainsQueryKey,
  useKeywordVolumesWithDomains,
} from "@/hooks/useKeywordVolumesWithDomains";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationControls } from "@/components/ui/PaginationControls/PaginationControls";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { KeywordVolumeCell } from "@/components/clicks/KeywordVolumeCell";
import { CopyableValue } from "@/components/clicks/CopyableValue";
import { KeywordVolumeSources } from "@/components/keyword-volume/KeywordVolumeSources";
import { ConfirmM1Month } from "@/components/keyword-volume/ConfirmM1Month";
import { BrandMismatches } from "@/components/keyword-volume/BrandMismatches";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { formatMonth } from "@/lib/keyword-volume-parser";
import {
  keywordVolumeService,
  type KeywordVolumeLimit,
} from "@/services/KeywordVolumeService";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 15;

export interface KeywordVolumeLoaderData {
  page: number;
  limit: KeywordVolumeLimit;
  search: string;
}

export async function keywordVolumeLoader({
  request,
}: LoaderFunctionArgs): Promise<KeywordVolumeLoaderData | Response> {
  try {
    await queryClient.prefetchQuery({
      queryKey: ["authUser"],
      queryFn: fetchUserProfile,
      staleTime: 1000 * 60 * 5,
    });
  } catch {
    return redirect("/login");
  }

  const url = new URL(request.url);
  const pageParam = Number(url.searchParams.get("page") || DEFAULT_PAGE);
  const limitParam = Number(url.searchParams.get("limit") || DEFAULT_LIMIT);
  const search = url.searchParams.get("q") ?? "";

  const page = Number.isNaN(pageParam) || pageParam < 1 ? DEFAULT_PAGE : pageParam;
  const limit = keywordVolumeService.normalizeLimit(limitParam);

  await queryClient.prefetchQuery({
    queryKey: getKeywordVolumesWithDomainsQueryKey(page, limit, search),
    queryFn: () => keywordVolumeService.getLatest(page, limit, search),
    staleTime: 1000 * 60 * 5,
  });

  return { page, limit, search };
}

const headClass = "px-4 py-3 text-xs font-medium tracking-wider text-muted-foreground uppercase";

export default function KeywordVolume() {
  const { page, limit, search } = useLoaderData() as KeywordVolumeLoaderData;
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const [, setSearchParams] = useSearchParams();
  const {
    loading,
    progress,
    status,
    pending,
    uploadedBatches,
    cancelImport,
    confirmImport,
    handleFileSelect,
  } = useKeywordVolumeImport();
  const { data } = useKeywordVolumesWithDomains({ page, limit, search });

  useEffect(() => {
    if (!uploadedBatches) return;
    void queryClient.invalidateQueries({ queryKey: ["keywordVolumesWithDomains"] });
    void queryClient.invalidateQueries({ queryKey: ["keywordVolumesForRows"] });
    revalidator.revalidate();
  }, [uploadedBatches, revalidator]);

  const rows = data?.rows ?? [];
  const totalCount = data?.totalCount ?? 0;
  const anchor = rows[0]?.anchor;
  const totalPages = Math.ceil(totalCount / limit);
  const isRouteLoading = navigation.state === "loading";
  const { rootRef, scrollContainerRef } = useTableScrollReset(`${page}-${limit}-${search}`);

  const setParam = (key: string, value: string) =>
    setSearchParams((prev) => {
      prev.set(key, value);
      if (key !== "page") prev.set("page", "1");
      return prev;
    });

  return (
    <div className="flex-1 overflow-x-hidden w-full">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Keyword Volume</h1>
        <p className="text-muted-foreground">
          Monthly search volume from keywordtool exports, by calendar month.
        </p>
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <ImportTabPanel
          title="Import keyword volume"
          description="One or several keywordtool exports (Monthly Data sheet: Keyword, Geo, M1–M12). xls, xlsx or csv."
          icon={BarChart3}
          loading={loading}
          accept=".xlsx,.xls,.csv"
          multiple
          buttonLabel="Select Excel or CSV files"
          onFileSelect={handleFileSelect}
        />
        <KeywordVolumeSources />
      </div>

      {pending && (
        <ConfirmM1Month
          key={pending.map((f) => f.fileName).join("|")}
          files={pending}
          loading={loading}
          onConfirm={(months) => void confirmImport(months)}
          onCancel={cancelImport}
        />
      )}

      <ImportProgress loading={loading} progress={progress} status={status} />

      <Tabs defaultValue="volumes" className="mt-6">
        <TabsList>
          <TabsTrigger value="volumes">Volumes</TabsTrigger>
          <TabsTrigger value="mismatches">Brand mismatches</TabsTrigger>
        </TabsList>
        <TabsContent value="volumes">
      <div className="mb-3 flex items-center gap-3">
        <Input
          placeholder="Search keyword"
          defaultValue={search}
          className="w-64"
          onKeyDown={(e) => {
            if (e.key === "Enter") setParam("q", (e.target as HTMLInputElement).value);
          }}
        />
        {anchor && (
          <span className="text-sm text-muted-foreground">
            M1 = {formatMonth(anchor)} (Google Ads month)
          </span>
        )}
      </div>

      <div
        ref={rootRef}
        className={cn(
          "relative scroll-mt-20 overflow-hidden rounded-lg border border-border bg-card transition-opacity",
          isRouteLoading && "opacity-60 pointer-events-none",
        )}
      >
        <div ref={scrollContainerRef} className="overflow-x-auto">
          <Table>
            <TableHeader className="border-b border-border bg-muted/50">
              <TableRow>
                <TableHead className={cn(headClass, "text-left")}>Keyword / Domain</TableHead>
                <TableHead className={cn(headClass, "text-left")}>Geo</TableHead>
                <TableHead className={cn(headClass, "text-right")}>Avg 12m</TableHead>
                <TableHead className={cn(headClass, "text-left")}>
                  Volume · M1{anchor ? ` = ${formatMonth(anchor)}` : ""} · trend 3vs3
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                    {isRouteLoading ? "Loading table..." : "No data yet. Upload an export or add a Google Sheet."}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={`${row.keyword_norm}|${row.geo}`} className="hover:bg-muted/40">
                    <TableCell className="px-4 py-3">
                      <div className="font-semibold text-foreground">{row.keyword}</div>
                      {row.domain_name ? (
                        <CopyableValue
                          value={row.domain_name}
                          noTruncate
                          className="text-xs font-medium text-primary"
                        />
                      ) : (
                        <span className="badge-muted mt-1 inline-flex items-center rounded px-2 py-0.5 text-xs font-medium">
                          No domain
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="px-4 py-3 text-foreground/80">{row.geo}</TableCell>
                    <TableCell className="px-4 py-3 text-right font-medium tabular-nums">
                      {row.volume_avg == null ? "—" : Number(row.volume_avg).toLocaleString("en-US")}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <KeywordVolumeCell months={row.months} anchor={row.anchor} trend={row.trend_3m} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between mt-6 px-4 pb-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Showing</span>
            <Select value={String(limit)} onValueChange={(v) => setParam("limit", v)}>
              <SelectTrigger className="w-[80px] h-9">
                <SelectValue placeholder={String(DEFAULT_LIMIT)} />
              </SelectTrigger>
              <SelectContent>
                {keywordVolumeService.getAllowedLimits().map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">rows of {totalCount.toLocaleString("en-US")}</span>
          </div>

          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p) => setParam("page", String(p))}
          />
        </div>
      </div>
        </TabsContent>
        <TabsContent value="mismatches">
          <BrandMismatches />
        </TabsContent>
      </Tabs>
    </div>
  );
}
